import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API, axios, usaNuovoToken } from '../utils/api';
import Icona from '../components/cliente/Icone';
import { DispositiviAccessoRapido } from '../components/features/DispositiviAccessoRapido';
import { DispositiviNotifiche } from '../components/features/DispositiviNotifiche';
import '../components/staff/scheda.css';
import '../components/staff/impostazioni.css';

// /profilo di admin e collaboratori, nello stile delle pagine dello staff: chi sono, numero per i clienti, Google
// Calendar (admin), il mio gruppo (responsabile di gruppo: prima stava nella vecchia /collaboratore/clienti), password
// e dispositivi con accesso rapido.

const errore = (e, riserva) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : riserva);

// Numero che i clienti vedono nella loro home (bottoni WhatsApp e Chiama del consulente)
const TelefonoPerClienti = () => {
  const [telefono, setTelefono] = useState('');
  const [salvato, setSalvato] = useState('');
  const [attesa, setAttesa] = useState(false);
  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => {
      const t = r.data?.user_info?.telefono || '';
      setTelefono(t);
      setSalvato(t);
    }).catch(() => {});
  }, []);
  const salva = async (e) => {
    e.preventDefault();
    setAttesa(true);
    try {
      const r = await axios.put(`${API}/profilo/telefono`, { telefono });
      setSalvato(r.data.telefono || '');
      setTelefono(r.data.telefono || '');
      toast.success(r.data.telefono ? 'Numero salvato' : 'Numero tolto');
    } catch (err) {
      toast.error(errore(err, 'Numero non salvato'));
    } finally {
      setAttesa(false);
    }
  };
  return (
    <section className="card">
      <div className="ttl"><div className="i g"><Icona nome="telefono" /></div><h2>Telefono per i clienti</h2></div>
      <p className="imp-nota">I vostri clienti lo vedono nella loro home, con i bottoni WhatsApp e Chiama. Se è vuoto vedono il numero dell'admin.</p>
      <form onSubmit={salva} className="imp-azioni">
        <input className="imp-input" style={{ flex: 1, minWidth: '12rem', width: 'auto' }} value={telefono} onChange={(e) => setTelefono(e.target.value)}
          placeholder="+39 333 123 4567" inputMode="tel" autoComplete="tel" aria-label="Telefono per i clienti" data-testid="telefono-staff" />
        <button type="submit" className="btn sm p" disabled={attesa || telefono.trim() === salvato}>Salva</button>
      </form>
    </section>
  );
};

// Il Google Calendar dell'admin che guarda: ognuno collega il suo (backend/server.py, "GOOGLE CALENDAR"). Ci arrivano
// le visite che accompagna e le voci dei clienti che segue; al ritorno da Google la pagina riceve ?google_connected.
export const GoogleCalendarStaff = () => {
  const [stato, setStato] = useState(null);
  const [attesa, setAttesa] = useState(false);
  const leggi = () => axios.get(`${API}/auth/google/status`).then((r) => setStato(r.data)).catch(() => setStato({ errore: true }));
  useEffect(() => {
    leggi();
    const q = new URLSearchParams(window.location.search);
    if (q.get('google_connected')) toast.success('Google Calendar collegato');
    if (q.get('google_error')) toast.error('Google Calendar non collegato: riprova');
    if (q.get('google_connected') || q.get('google_error')) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const azione = async (fn) => {
    setAttesa(true);
    try { await fn(); } catch (e) { toast.error(errore(e, 'Non riuscito')); } finally { setAttesa(false); }
  };
  const collega = () => azione(async () => { const r = await axios.get(`${API}/auth/google/login`); window.location.href = r.data.authorization_url; });
  const sincronizza = () => azione(async () => { const r = await axios.post(`${API}/calendar/sync-all`); toast.success(r.data.message); });
  const scollega = () => azione(async () => { await axios.post(`${API}/auth/google/disconnect`); await leggi(); toast('Google Calendar scollegato'); });
  if (!stato || stato.errore) return null;
  return (
    <section className="card" data-testid="google-calendar-staff">
      <div className="ttl"><div className="i b"><Icona nome="calendario" /></div><h2>Il mio Google Calendar</h2>
        <span className={`tag ${stato.connected ? 't-g' : 't-o'}`}>{stato.connected ? 'collegato' : 'non collegato'}</span></div>
      <p className="imp-nota">
        {stato.connected
          ? <>Collegato con <b>{stato.email}</b>. Ci arrivano le visite che accompagni e quelle dei clienti che segui.</>
          : 'Ognuno collega il suo: ci arrivano le visite che accompagni e quelle dei clienti che segui.'}
      </p>
      {stato.puo_collegare === false && !stato.connected && (
        <p className="imp-nota w" style={{ marginTop: '.5rem' }}>La tua email non è ancora tra quelle ammesse: va aggiunta a <code>GOOGLE_CALENDAR_ADMIN_EMAIL</code> su Railway.</p>
      )}
      <div className="imp-azioni">
        {stato.connected ? (
          <>
            <button type="button" className="btn sm p" disabled={attesa} onClick={sincronizza}>Sincronizza tutto</button>
            <button type="button" className="btn sm" disabled={attesa} onClick={scollega}>Scollega</button>
          </>
        ) : (
          <button type="button" className="btn sm p" disabled={attesa || stato.puo_collegare === false} onClick={collega}>Collega il mio Google Calendar</button>
        )}
      </div>
    </section>
  );
};

// Il mio gruppo (solo il responsabile di gruppo): chi c'è, togli; aggiungi chi non ha ancora un gruppo. Spostare
// qualcuno da un altro gruppo lo fa l'admin in Collaboratori.
export const IlMioGruppo = () => {
  const [membri, setMembri] = useState(null);
  const [liberi, setLiberi] = useState([]);
  const carica = useCallback(() => Promise.all([
    axios.get(`${API}/collaboratore/miei-collaboratori`), axios.get(`${API}/collaboratore/collaboratori-non-assegnati`),
  ]).then(([m, l]) => { setMembri(m.data || []); setLiberi(l.data || []); }).catch(() => { setMembri([]); setLiberi([]); }), []);
  useEffect(() => { carica(); }, [carica]);
  const cambia = async (url, testo) => {
    try { await axios.post(`${API}/collaboratore/${url}`); toast.success(testo); carica(); } catch (e) { toast.error(errore(e, 'Non riuscito')); }
  };
  if (!membri) return null;
  const nome = (c) => [c.nome, c.cognome].filter(Boolean).join(' ');
  return (
    <section className="card" data-testid="il-mio-gruppo">
      <div className="ttl"><div className="i v"><Icona nome="persone" /></div><h2>Il mio gruppo <small>{membri.length}</small></h2></div>
      <p className="imp-nota" style={{ marginBottom: '.7rem' }}>Vedete i clienti dei collaboratori del vostro gruppo in Clienti e la loro giornata in Oggi.</p>
      {!membri.length && <p className="vuoto">Nessuno nel gruppo per ora.</p>}
      <div className="imp-righe">
        {membri.map((c) => (
          <div key={c.id} data-testid={`membro-${c.id}`}>
            <b>{nome(c)}</b><span className="descr">{[c.email, c.num_clienti != null ? `${c.num_clienti} clienti` : null].filter(Boolean).join(' · ')}</span>
            <div className="azioni"><button type="button" className="btn xs rosso-vuoto" onClick={() => cambia(`rimuovi-dal-gruppo/${c.id}`, `${nome(c)} non è più nel gruppo`)}>Togli</button></div>
          </div>
        ))}
      </div>
      {liberi.length > 0 && (
        <>
          <p className="imp-gruppo" style={{ marginTop: '1rem' }}>Senza gruppo</p>
          <div className="imp-righe">
            {liberi.map((c) => (
              <div key={c.id} data-testid={`libero-${c.id}`}>
                <b>{nome(c)}</b><span className="descr">{c.email}</span>
                <div className="azioni"><button type="button" className="btn xs p" onClick={() => cambia(`aggiungi-al-gruppo/${c.id}`, `${nome(c)} è nel vostro gruppo`)}>Aggiungi</button></div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

// Cambio della password (POST /auth/password, backend/profilo.py): per chi è entrato con una password temporanea
export const CambiaPassword = () => {
  const [dati, setDati] = useState({ attuale: '', nuova: '' });
  const [attesa, setAttesa] = useState(false);
  const salva = async (e) => {
    e.preventDefault();
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/auth/password`, dati);
      usaNuovoToken(r.data?.access_token); // le altre sessioni si chiudono, questa continua
      setDati({ attuale: '', nuova: '' });
      toast.success('Password cambiata');
    } catch (err) {
      toast.error(errore(err, 'Password non cambiata'));
    } finally {
      setAttesa(false);
    }
  };
  const campo = (k) => ({ value: dati[k], onChange: (e) => setDati({ ...dati, [k]: e.target.value }) });
  return (
    <section className="card">
      <div className="ttl"><div className="i"><Icona nome="chiave" /></div><h2>Password</h2></div>
      <form onSubmit={salva} className="imp-campi">
        <label>Quella di adesso<input type="password" autoComplete="current-password" aria-label="Password di adesso" {...campo('attuale')} /></label>
        <label>La nuova<input type="password" autoComplete="new-password" aria-label="Nuova password" minLength={8} {...campo('nuova')} /></label>
        <div style={{ alignSelf: 'end' }}><button type="submit" className="btn sm p" disabled={attesa || !dati.attuale || dati.nuova.length < 8}>Cambia</button></div>
      </form>
    </section>
  );
};

const ProfiloStaff = () => {
  const { user, userType } = useAuth();
  const nome = [user?.nome, user?.cognome].filter(Boolean).join(' ');
  return (
    <main className="st wrap profilo-staff" data-testid="profilo-staff">
      <div className="chi-sono" style={{ marginBottom: '1.2rem' }}>
        <div className="av">{(user?.nome || '?').charAt(0).toUpperCase()}</div>
        <div style={{ minWidth: 0 }}>
          <div className="k">{userType === 'admin' ? 'Admin' : user?.responsabile_gruppo ? 'Responsabile di gruppo' : 'Collaboratore'}</div>
          <h1>{nome}</h1>
          <p>{user?.email}</p>
        </div>
      </div>
      <div className="imp-griglia">
        <TelefonoPerClienti />
        {userType === 'admin' && <GoogleCalendarStaff />}
        {userType === 'collaboratore' && user?.responsabile_gruppo && <IlMioGruppo />}
        <CambiaPassword />
        <section className="card accesso-rapido-staff"><DispositiviNotifiche /></section>
        <section className="card accesso-rapido-staff"><DispositiviAccessoRapido /></section>
      </div>
    </main>
  );
};

// /profilo: il cliente vede la sua scheda, admin e collaboratori questa pagina
const Profilo = ({ scheda }) => {
  const { userType } = useAuth();
  return userType === 'cliente' ? scheda : <ProfiloStaff />;
};

export default Profilo;
